import { z } from 'zod';
import type { DefenseModule, WeaponType } from './loadout.js';
import { hoursToMs, researchDurationH } from '../universe.js';
import { rpCostOfTier, LabClientSchema, DiscoveryClientSchema, ResearchLogEntrySchema } from './research-labs.js';
import { CATALOG_NEW, LEGACY_META, TECH_AREAS, type TechArea, type TechKind, type TechRarity } from './research-catalog.js';
export { TECH_AREAS, AREA_NAMES, AREA_BLURB, MAX_TECH_TIER, type TechArea, type TechKind, type TechRarity } from './research-catalog.js';

export const TechAreaSchema = z.enum(TECH_AREAS);

// ---- M9: research & tech tree ----

export const TechBranchSchema = z.enum(['economy', 'military', 'exploration', 'diplomacy', 'science']);
export type TechBranch = z.infer<typeof TechBranchSchema>;

export interface TechEffects {
  /** Multiplier bonus on mining station metal output (0.1 = +10%). */
  miningOutput?: number;
  energyOutput?: number;
  /** Reduction of station + outpost energy upkeep (0.15 = −15%). */
  infrastructureUpkeep?: number;
  fleetUpkeep?: number;
  /** Fleet speed bonus in ly/h (added to the slowest ship's speed). */
  travelSpeed?: number;
  surveyTime?: number;
  weaponDamage?: number;
  shieldStrength?: number;
  armorBonus?: number;
  tradeIncome?: number;
  extraTradeAgreements?: number;
  unlocksCruiser?: boolean;
  /** M13: szabályt módosító technológiák (nem csak százalék). */
  unlocksWeapons?: WeaponType[];
  unlocksModules?: DefenseModule[];
  /** Egyszerre fenntartható kereskedelmi útvonalak száma. */
  tradeRoutes?: number;
  /** A kereskedelmi útvonal legnagyobb hossza (csillagközi ugrás). */
  routeRange?: number;
  /** Zárlat a tranzitrendszerekben is (alapból csak az útvonal végpontjain). */
  unlocksInterdiction?: boolean;
  unlocksMegaprojects?: boolean;
  /** A hajók ellátási kerete (a fölötte lévők fenntartása drágább). */
  supplyCap?: number;
  /** Track 8: kutatási rendszer. */
  /** Extra párhuzamos kutatási hely. */
  researchSlots?: number;
  /** Fix RP/óra a birodalomnak. */
  researchPoints?: number;
  /** A legmagasabb építhető laboratóriumi szint. */
  labTier?: number;
  /** A laboratóriumok RP-termelésének szorzója (0.1 = +10%). */
  labOutput?: number;
  /** Extra várólista-hely. */
  queueSlots?: number;
  /** A felmérés a rejtett leleteket (prekurzor-műtárgyak) is felismeri. */
  deepScan?: boolean;
  /** Research v2: raktárkapacitás (fém és energia) szorzó-bónusza (0.3 = +30%). */
  storage?: number;
  /** Research v2: a kártyás "kéz" mérete (+1 kártya a lehetőségek között). */
  handSize?: number;
}

export interface TechDefinition {
  id: string;
  name: string;
  description: string;
  branch: TechBranch;
  tier: number;
  cost: { metal: number; energy: number; rp: number };
  /** Szabályt módosít (új lehetőség) vagy csak kisebb támogató csomópont (százalék). */
  role: 'rule' | 'support';
  /** Research v2: the area (own research slot, own RP), the kind (fixed spine / card from the hand) and the rarity. */
  area: TechArea;
  kind: TechKind;
  rarity: TechRarity;
  /** Rövid, konkrét lista arról, mit old fel / változtat meg. */
  unlocks: string[];
  /** Csak felfedezésből szerezhető, nem kutatható. */
  discoveryOnly?: boolean;
  /** Research time at GAME_SPEED 1; fixed by tier (universe.ts). The server divides it by GAME_SPEED. */
  timeMs: number;
  requires: string[];
  effects: TechEffects;
}


const WEAPON_NAMES: Record<string, string> = { kinetic: 'kinetikus ágyú', beam: 'energiasugár', missile: 'rakéta' };
const MODULE_NAMES: Record<string, string> = { none: '—', plating: 'páncélozás', shield: 'pajzsmodul', pointdef: 'pontvédelem', ecm: 'ECM' };

/** Effects that open a new possibility (as opposed to a plain percentage). */
const RULE_KEYS: Array<keyof TechEffects> = [
  'unlocksCruiser', 'unlocksWeapons', 'unlocksModules', 'tradeRoutes', 'routeRange', 'unlocksInterdiction', 'unlocksMegaprojects',
  'supplyCap', 'extraTradeAgreements', 'researchSlots', 'labTier', 'queueSlots', 'deepScan', 'storage', 'handSize',
];

export function techRole(e: TechEffects): 'rule' | 'support' {
  return RULE_KEYS.some(k => e[k] !== undefined && e[k] !== 0 && e[k] !== false) ? 'rule' : 'support';
}

/** Short Hungarian lines about what a tech opens up or changes (shown on the card). */
export function techUnlocks(e: TechEffects): string[] {
  const out: string[] = [];
  if (e.unlocksCruiser) out.push('Új hajó: cirkáló');
  if (e.unlocksWeapons?.length) out.push(`Új fegyver: ${e.unlocksWeapons.map(w => WEAPON_NAMES[w] ?? w).join(', ')}`);
  if (e.unlocksModules?.length) out.push(`Új modul: ${e.unlocksModules.map(m => MODULE_NAMES[m] ?? m).join(', ')}`);
  if (e.unlocksInterdiction) out.push('Új szabály: zárlat a tranzitrendszerekben');
  if (e.unlocksMegaprojects) out.push('Új lehetőség: megaprojektek');
  if (e.tradeRoutes) out.push(`+${e.tradeRoutes} kereskedelmi útvonal`);
  if (e.routeRange) out.push(`Útvonalhossz +${e.routeRange} ugrás`);
  if (e.extraTradeAgreements) out.push(`+${e.extraTradeAgreements} kereskedelmi megállapodás`);
  if (e.supplyCap) out.push(`Ellátási keret +${e.supplyCap} hajó`);
  if (e.labTier) out.push(`Új épületszint: ${e.labTier}. szintű laboratórium`);
  if (e.researchSlots) out.push(`+${e.researchSlots} párhuzamos kutatási hely`);
  if (e.queueSlots) out.push(`Kutatási várólista +${e.queueSlots} hely`);
  if (e.deepScan) out.push('Új lehetőség: rejtett leletek felismerése');
  if (e.storage) out.push(`Raktárkapacitás +${Math.round(e.storage * 100)}% (fém és energia)`);
  if (e.handSize) out.push(`+${e.handSize} kártya a lehetőségek között`);
  if (e.researchPoints) out.push(`+${e.researchPoints} RP/óra`);
  if (e.labOutput) out.push(`Laboratóriumok +${Math.round(e.labOutput * 100)}% RP`);
  if (e.travelSpeed) out.push(`Flották +${e.travelSpeed} ly/h`);
  if (e.miningOutput) out.push(`Bányák +${Math.round(e.miningOutput * 100)}% fém`);
  if (e.energyOutput) out.push(`Energiaállomások +${Math.round(e.energyOutput * 100)}%`);
  if (e.infrastructureUpkeep) out.push(`Infrastruktúra-fenntartás −${Math.round(e.infrastructureUpkeep * 100)}%`);
  if (e.fleetUpkeep) out.push(`Flottafenntartás −${Math.round(e.fleetUpkeep * 100)}%`);
  if (e.surveyTime) out.push(`Felmérés ${Math.round(e.surveyTime * 100)}%-kal gyorsabb`);
  if (e.weaponDamage) out.push(`Sebzés +${Math.round(e.weaponDamage * 100)}%`);
  if (e.shieldStrength) out.push(`Pajzs +${Math.round(e.shieldStrength * 100)}%`);
  if (e.armorBonus) out.push(`Páncél +${e.armorBonus}`);
  if (e.tradeIncome) out.push(`Kereskedelmi bevétel +${Math.round(e.tradeIncome * 100)}%`);
  return out;
}

type RawTech = Omit<TechDefinition, 'timeMs' | 'role' | 'unlocks' | 'cost' | 'area' | 'kind' | 'rarity' | 'branch'> & { branch: TechBranch; cost: { metal: number; energy: number } };

/** Metal price by tier for the research-v2 techs (energy is ~65% of it); the pre-v2 techs keep their own prices. */
export const TECH_METAL_BY_TIER: Record<number, number> = { 1: 300, 2: 650, 3: 1100, 4: 2000, 5: 3500 };
const metalOf = (tier: number, cf = 1) => Math.round((TECH_METAL_BY_TIER[tier] ?? 3500) * cf / 10) * 10;
const energyOf = (tier: number, cf = 1) => Math.round((TECH_METAL_BY_TIER[tier] ?? 3500) * 0.65 * cf / 10) * 10;

const LEGACY_TECHS: TechDefinition[] = ([
  // Economy
  { id: 'deep_core_mining', name: 'Mélymagfúrás', branch: 'economy', tier: 1, cost: { metal: 300, energy: 150 }, requires: [],
    description: 'A bányaállomások 15%-kal több fémet termelnek.', effects: { miningOutput: 0.15 } },
  { id: 'fusion_grid', name: 'Fúziós hálózat', branch: 'economy', tier: 1, cost: { metal: 250, energy: 200 }, requires: [],
    description: 'Az energiaállomások 20%-kal többet termelnek.', effects: { energyOutput: 0.2 } },
  { id: 'automated_maintenance', name: 'Automatizált karbantartás', branch: 'economy', tier: 2, cost: { metal: 500, energy: 300 }, requires: ['fusion_grid'],
    description: 'Az állomások és előőrsök energiafenntartása 25%-kal csökken.', effects: { infrastructureUpkeep: 0.25 } },
  { id: 'nanoforges', name: 'Nanokohók', branch: 'economy', tier: 3, cost: { metal: 900, energy: 600 }, requires: ['deep_core_mining', 'automated_maintenance'],
    description: 'A bányaállomások további 25%-kal több fémet termelnek.', effects: { miningOutput: 0.25 } },
  { id: 'megastructures', name: 'Megastruktúrák', branch: 'economy', tier: 4, cost: { metal: 1500, energy: 1000 }, requires: ['nanoforges', 'fusion_grid'],
    description: 'Feloldja a megaprojekteket: hatalmas, hosszú építkezések tartós hatással (raktár, termelés, ellátás).', effects: { unlocksMegaprojects: true } },

  // Science (Track 8): labs, slots, queue, discoveries
  { id: 'lab_networks', name: 'Laboratóriumhálózat', branch: 'science', tier: 1, cost: { metal: 250, energy: 150 }, requires: [],
    description: 'Összekapcsolt adatközpontok: +1 RP/óra a birodalomnak.', effects: { researchPoints: 1 } },
  { id: 'orbital_labs', name: 'Orbitális laboratóriumok', branch: 'science', tier: 2, cost: { metal: 500, energy: 350 }, requires: ['lab_networks'],
    description: 'Feloldja a 2. szintű laboratóriumot (10 RP/óra). A 2. szintű labor második kutatási helyet is ad.', effects: { labTier: 2 } },
  { id: 'parallel_research', name: 'Párhuzamos kutatás', branch: 'science', tier: 2, cost: { metal: 550, energy: 400 }, requires: ['lab_networks'],
    description: 'Egy újabb kutatási hely: két projekt futhat egyszerre.', effects: { researchSlots: 1 } },
  { id: 'research_automation', name: 'Kutatásautomatizálás', branch: 'science', tier: 3, cost: { metal: 800, energy: 600 }, requires: ['parallel_research'],
    description: 'A kutatási várólista 2 helyett 6 projektet tart; a felszabaduló hely automatikusan új projektet indít.', effects: { queueSlots: 4 } },
  { id: 'quantum_labs', name: 'Kvantumlaboratóriumok', branch: 'science', tier: 3, cost: { metal: 1100, energy: 800 }, requires: ['orbital_labs', 'fusion_grid'],
    description: 'Feloldja a 3. szintű laboratóriumot (22 RP/óra), és minden laboratórium 10%-kal többet termel.', effects: { labTier: 3, labOutput: 0.1 } },
  { id: 'precursor_lattice', name: 'Prekurzor-rács', branch: 'science', tier: 3, cost: { metal: 0, energy: 0 }, requires: [], discoveryOnly: true,
    description: 'Ősi számítási rács egy műtárgyból. Csak felfedezéssel szerezhető: +1 kutatási hely és +3 RP/óra.', effects: { researchSlots: 1, researchPoints: 3 } },

  // Military
  { id: 'kinetic_weapons', name: 'Kinetikus fegyverek', branch: 'military', tier: 1, cost: { metal: 350, energy: 150 }, requires: [],
    description: 'A hadihajók sebzése 10%-kal nő.', effects: { weaponDamage: 0.1 } },
  { id: 'deflector_shields', name: 'Terelőpajzsok', branch: 'military', tier: 1, cost: { metal: 300, energy: 250 }, requires: [],
    description: 'A hajók pajzsa 20%-kal erősebb.', effects: { shieldStrength: 0.2 } },
  { id: 'composite_armor', name: 'Kompozit páncél', branch: 'military', tier: 2, cost: { metal: 600, energy: 250 }, requires: ['kinetic_weapons'],
    description: 'A hajók páncélzata +3.', effects: { armorBonus: 3 } },
  { id: 'capital_ships', name: 'Csatahajó-építés', branch: 'military', tier: 2, cost: { metal: 800, energy: 400 }, requires: ['deflector_shields'],
    description: 'Feloldja a cirkálók gyártását.', effects: { unlocksCruiser: true } },
  { id: 'fleet_logistics', name: 'Flottalogisztika', branch: 'military', tier: 3, cost: { metal: 700, energy: 500 }, requires: ['composite_armor'],
    description: 'A flották energiafenntartása 30%-kal csökken, és 6-tal több hajó látható el a drágább fenntartási sáv előtt.', effects: { fleetUpkeep: 0.3, supplyCap: 6 } },
  { id: 'plasma_lances', name: 'Plazmalándzsák', branch: 'military', tier: 3, cost: { metal: 1200, energy: 800 }, requires: ['composite_armor', 'capital_ships'],
    description: 'A hadihajók sebzése további 20%-kal nő.', effects: { weaponDamage: 0.2 } },
  { id: 'beam_weapons', name: 'Energiasugarak', branch: 'military', tier: 2, cost: { metal: 600, energy: 400 }, requires: ['kinetic_weapons'],
    description: 'Feloldja az energiasugár fegyvert: rövidebb hatótáv, átüti a páncélozást, a pajzsmodul ellen gyenge.', effects: { unlocksWeapons: ['beam'] } },
  { id: 'missile_tubes', name: 'Rakétaindítók', branch: 'military', tier: 2, cost: { metal: 650, energy: 400 }, requires: ['kinetic_weapons'],
    description: 'Feloldja a rakétát: 30%-kal nagyobb hatótáv, de a pontvédelem és az ECM gyengíti.', effects: { unlocksWeapons: ['missile'] } },
  { id: 'reactive_plating', name: 'Reaktív páncélozás', branch: 'military', tier: 3, cost: { metal: 800, energy: 450 }, requires: ['composite_armor'],
    description: 'Feloldja a páncélozás modult: a kinetikus lövedékek ellen erős, az energiasugár ellen gyenge.', effects: { unlocksModules: ['plating'] } },
  { id: 'shield_matrix', name: 'Pajzsmátrix', branch: 'military', tier: 2, cost: { metal: 550, energy: 450 }, requires: ['deflector_shields'],
    description: 'Feloldja a pajzsmodult: +40% pajzs, az energiasugár ellen erős, a kinetikus ágyú átüti.', effects: { unlocksModules: ['shield'] } },
  { id: 'point_defense', name: 'Pontvédelmi hálózat', branch: 'military', tier: 2, cost: { metal: 500, energy: 450 }, requires: ['deflector_shields'],
    description: 'Feloldja a pontvédelmet: a rakéták sebzésének 60%-át lelövi.', effects: { unlocksModules: ['pointdef'] } },
  { id: 'interdiction_field', name: 'Zárlati tér', branch: 'military', tier: 3, cost: { metal: 900, energy: 600 }, requires: ['capital_ships', 'warp_drive'],
    description: 'A blokád a kereskedelmi útvonalak tranzitrendszereiben is érvényesíthető, nem csak a végpontjain.', effects: { unlocksInterdiction: true } },

  // Exploration
  { id: 'sensor_arrays', name: 'Szenzorhálók', branch: 'exploration', tier: 1, cost: { metal: 200, energy: 150 }, requires: [],
    description: 'A felmérés 40%-kal gyorsabb.', effects: { surveyTime: 0.4 } },
  { id: 'warp_drive', name: 'Hajtóműhangolás', branch: 'exploration', tier: 2, cost: { metal: 500, energy: 400 }, requires: ['sensor_arrays'],
    description: 'Minden flotta +3 ly/h sebességet kap, így az ugrások rövidebb ideig tartanak.', effects: { travelSpeed: 3 } },
  { id: 'ecm_suite', name: 'Zavaróberendezés (ECM)', branch: 'exploration', tier: 2, cost: { metal: 550, energy: 450 }, requires: ['sensor_arrays'],
    description: 'Feloldja az ECM modult: +10% kitérés, a rakéták sebzését 40%-kal csökkenti.', effects: { unlocksModules: ['ecm'] } },

  { id: 'xenoarchaeology', name: 'Xenoarcheológia', branch: 'exploration', tier: 2, cost: { metal: 450, energy: 350 }, requires: ['sensor_arrays'],
    description: 'A felmérés felismeri a rejtett leleteket: prekurzor-műtárgyak is feltárulnak (a már felmért rendszerekben is).', effects: { deepScan: true } },
  { id: 'void_compass', name: 'Űriránytű', branch: 'exploration', tier: 3, cost: { metal: 0, energy: 0 }, requires: [], discoveryOnly: true,
    description: 'Ősi navigációs műszer. Csak felfedezéssel szerezhető: minden flotta +4 ly/h sebességet kap, és a kereskedelmi útvonalak 2 ugrással hosszabbak lehetnek.', effects: { travelSpeed: 4, routeRange: 2 } },

  // Diplomacy
  { id: 'trade_networks', name: 'Kereskedelmi hálózatok', branch: 'diplomacy', tier: 1, cost: { metal: 300, energy: 200 }, requires: [],
    description: 'A kereskedelmi bevétel 50%-kal nő, és +1 megállapodás köthető.', effects: { tradeIncome: 0.5, extraTradeAgreements: 1, tradeRoutes: 1 } },
  { id: 'merchant_fleets', name: 'Kereskedőflották', branch: 'diplomacy', tier: 2, cost: { metal: 600, energy: 350 }, requires: ['trade_networks'],
    description: 'Két újabb kereskedelmi útvonal tartható fenn.', effects: { tradeRoutes: 2 } },
  { id: 'deep_space_freight', name: 'Mélyűri teherszállítás', branch: 'diplomacy', tier: 3, cost: { metal: 1000, energy: 700 }, requires: ['merchant_fleets', 'warp_drive'],
    description: 'A kereskedelmi útvonalak 3 helyett 6 ugrásig érhetnek, és eggyel több útvonal tartható fenn.', effects: { routeRange: 3, tradeRoutes: 1 } },
] satisfies RawTech[]).map(t => ({
  ...t,
  ...LEGACY_META[t.id]!,
  cost: { ...t.cost, rp: 'discoveryOnly' in t && t.discoveryOnly ? 0 : rpCostOfTier(t.tier) },
  timeMs: hoursToMs(researchDurationH(t.tier)),
  role: techRole(t.effects),
  unlocks: techUnlocks(t.effects),
}));

const NEW_TECHS: TechDefinition[] = CATALOG_NEW.map(c => ({
  id: c.id, name: c.name, branch: c.theme, tier: c.tier, requires: c.requires, effects: c.effects,
  area: c.area, kind: c.kind, rarity: c.rarity ?? 'common',
  description: [c.note, techUnlocks(c.effects).join(' · ')].filter(Boolean).join(' '),
  cost: { metal: metalOf(c.tier, c.cf), energy: energyOf(c.tier, c.cf), rp: rpCostOfTier(c.tier) },
  timeMs: hoursToMs(researchDurationH(c.tier) * (c.tf ?? 1)),
  role: techRole(c.effects),
  unlocks: techUnlocks(c.effects),
}));

export const TECHS: TechDefinition[] = [...LEGACY_TECHS, ...NEW_TECHS];

export const TECH_BY_ID = new Map(TECHS.map(t => [t.id, t]));
export const techsOfArea = (area: TechArea): TechDefinition[] => TECHS.filter(t => t.area === area);

export interface TechModifiers {
  miningOutput: number; energyOutput: number; infrastructureUpkeep: number; fleetUpkeep: number;
  travelSpeed: number; surveyTime: number; weaponDamage: number; shieldStrength: number; armorBonus: number;
  tradeIncome: number; extraTradeAgreements: number; unlocksCruiser: boolean;
  /** M13 */
  unlockedWeapons: WeaponType[]; unlockedModules: DefenseModule[];
  tradeRoutes: number; routeRange: number; interdiction: boolean; megaprojects: boolean; supplyCap: number;
  /** Track 8 */
  researchSlots: number; researchPoints: number; labTier: number; labOutput: number; queueSlots: number; deepScan: boolean;
  /** Research v2 */
  storage: number; handSize: number;
}

/** Slots, queue places and flat RP the finished techs of ONE area give (the slot/RP rules are per area). */
export interface AreaModifiers { researchSlots: number; queueSlots: number; researchPoints: number; handSize: number }

export function areaModifiers(done: Iterable<string>): Record<TechArea, AreaModifiers> {
  const out = Object.fromEntries(TECH_AREAS.map(a => [a, { researchSlots: 0, queueSlots: 0, researchPoints: 0, handSize: 0 }])) as Record<TechArea, AreaModifiers>;
  for (const id of done) {
    const t = TECH_BY_ID.get(id);
    if (!t) continue;
    const a = out[t.area];
    a.researchSlots += t.effects.researchSlots ?? 0;
    a.queueSlots += t.effects.queueSlots ?? 0;
    a.researchPoints += t.effects.researchPoints ?? 0;
    a.handSize += t.effects.handSize ?? 0;
  }
  return out;
}

/** Sums the effects of finished techs. Reductions are capped so nothing ever becomes free/instant. */
export function techModifiers(done: Iterable<string>): TechModifiers {
  const m: TechModifiers = {
    miningOutput: 0, energyOutput: 0, infrastructureUpkeep: 0, fleetUpkeep: 0, travelSpeed: 0, surveyTime: 0,
    weaponDamage: 0, shieldStrength: 0, armorBonus: 0, tradeIncome: 0, extraTradeAgreements: 0, unlocksCruiser: false,
    unlockedWeapons: ['kinetic'], unlockedModules: ['none'], tradeRoutes: 0, routeRange: 0, interdiction: false, megaprojects: false, supplyCap: 0,
    researchSlots: 0, researchPoints: 0, labTier: 1, labOutput: 0, queueSlots: 0, deepScan: false, storage: 0, handSize: 0,
  };
  for (const id of done) {
    const e = TECH_BY_ID.get(id)?.effects;
    if (!e) continue;
    for (const k of Object.keys(e) as Array<keyof TechEffects>) {
      if (k === 'unlocksCruiser') m.unlocksCruiser = m.unlocksCruiser || !!e.unlocksCruiser;
      else if (k === 'unlocksWeapons') { for (const w of e.unlocksWeapons ?? []) if (!m.unlockedWeapons.includes(w)) m.unlockedWeapons.push(w); }
      else if (k === 'unlocksModules') { for (const x of e.unlocksModules ?? []) if (!m.unlockedModules.includes(x)) m.unlockedModules.push(x); }
      else if (k === 'unlocksInterdiction') m.interdiction = m.interdiction || !!e.unlocksInterdiction;
      else if (k === 'unlocksMegaprojects') m.megaprojects = m.megaprojects || !!e.unlocksMegaprojects;
      else if (k === 'deepScan') m.deepScan = m.deepScan || !!e.deepScan;
      else if (k === 'labTier') m.labTier = Math.max(m.labTier, e.labTier ?? 1);
      else m[k] += e[k] ?? 0;
    }
  }
  for (const k of ['infrastructureUpkeep', 'fleetUpkeep', 'surveyTime'] as const) m[k] = Math.min(m[k], 0.6);
  return m;
}

const TechClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  branch: TechBranchSchema,
  /** Research v2 */
  area: TechAreaSchema,
  kind: z.enum(['spine', 'card']),
  rarity: z.enum(['common', 'rare']),
  tier: z.number().int(),
  cost: z.object({ metal: z.number().int(), energy: z.number().int(), rp: z.number().int() }),
  /** Price after a pending "wreck field" discount (what the next start would really cost). */
  effectiveCost: z.object({ metal: z.number().int(), energy: z.number().int(), rp: z.number().int() }),
  timeMs: z.number().int(),
  requires: z.array(z.string()),
  role: z.enum(['rule', 'support']),
  unlocks: z.array(z.string()),
  discoveryOnly: z.boolean(),
  status: z.enum(['done', 'researching', 'queued', 'available', 'locked']),
  /** Why a `locked` tech cannot be started now (null when it is not locked). */
  lockReason: z.enum(['prerequisite', 'tier', 'not_in_hand', 'discovery']).nullable().optional(),
  /** Names of the missing prerequisites. */
  missing: z.array(z.string()).optional(),
  /** A card tech that is in the area's current hand. */
  inHand: z.boolean().optional(),
  /** Names of the techs this one opens up (those that list it as a prerequisite). */
  opens: z.array(z.string()).optional(),
  completesAt: z.string().datetime().nullable(),
  queuePosition: z.number().int().nullable(),
});

const ActiveProjectSchema = z.object({ techId: z.string(), startedAt: z.string().datetime(), completesAt: z.string().datetime(), refundable: z.boolean() });
const QueueEntrySchema = z.object({ techId: z.string(), position: z.number().int(), blockedBy: z.string().nullable() });
const RpSourceSchema = z.object({ kind: z.enum(['home', 'lab', 'tech', 'leader']), label: z.string(), perHour: z.number(), params: z.record(z.string(), z.union([z.string(), z.number()])).optional() });

/** One research area: its own slots, queue, RP stock and card hand. */
export const AreaStateSchema = z.object({
  area: TechAreaSchema,
  name: z.string(),
  slots: z.object({ total: z.number().int(), used: z.number().int(), sources: z.array(z.object({ label: z.string(), count: z.number().int() })) }),
  queueLimit: z.number().int(),
  active: z.array(ActiveProjectSchema),
  queue: z.array(QueueEntrySchema),
  rp: z.object({ balance: z.number(), perHour: z.number(), sources: z.array(RpSourceSchema), surveyData: z.number().int().optional(), leaderBonus: z.number().optional() }),
  hand: z.object({ cards: z.array(z.string()), size: z.number().int(), drawSeq: z.number().int(), pity: z.number().int(), pityAt: z.number().int() }),
  /** Highest open tier, finished techs per tier and the gate size. */
  tiers: z.object({ open: z.number().int(), done: z.record(z.string(), z.number().int()), gate: z.number().int() }),
  progress: z.object({ done: z.number().int(), total: z.number().int(), spineDone: z.number().int(), spineTotal: z.number().int() }),
  labs: z.object({ count: z.number().int(), max: z.number().int() }),
});
export type AreaState = z.infer<typeof AreaStateSchema>;

export const ResearchStateSchema = z.object({
  techs: z.array(TechClientSchema),
  /** Research v2: the three areas, each with its own research place, queue, RP and hand. */
  areas: z.array(AreaStateSchema),
  /** The first running project (kept for older clients). */
  current: z.object({ techId: z.string(), startedAt: z.string().datetime(), completesAt: z.string().datetime() }).nullable(),
  active: z.array(ActiveProjectSchema),
  queue: z.array(QueueEntrySchema),
  queueLimit: z.number().int(),
  /** Totals over all areas (older clients). */
  slots: z.object({ total: z.number().int(), used: z.number().int(), sources: z.array(z.object({ label: z.string(), count: z.number().int() })) }),
  rp: z.object({
    balance: z.number(),
    perHour: z.number(),
    sources: z.array(RpSourceSchema),
    /** Lab energy upkeep per hour and the RP per hour committed to running projects' remaining cost: the "net" view. */
    energyUpkeepPerHour: z.number().int(),
    queuedCost: z.number().int(),
  }),
  labs: z.array(LabClientSchema),
  /** Owned systems that can still get a lab. */
  labSites: z.array(z.object({ systemId: z.string().uuid(), systemName: z.string() })),
  labBuild: z.object({ metal: z.number().int(), energy: z.number().int(), timeMs: z.number().int(), rpPerHour: z.number(), energyUpkeep: z.number().int(), canBuild: z.boolean() }),
  discoveries: z.array(DiscoveryClientSchema),
  log: z.array(ResearchLogEntrySchema),
});
export type ResearchState = z.infer<typeof ResearchStateSchema>;

export const StartResearchSchema = z.object({ techId: z.string().min(1).max(64), idempotencyKey: z.string().min(1).max(128) });
export type StartResearch = z.infer<typeof StartResearchSchema>;
